import type { ClientData } from '@/lib/client';

export interface UpfrontBillingInput {
  upfront_billing_eligible: boolean;
  retail_price: number;
  discounted_price?: number | null;
  upfront_months?: number | null;
}

export interface UpfrontBillingResult {
  show: boolean;
  months: number | null;
  totalDue: number | null;
}

/**
 * Determines whether to surface an upfront billing requirement on a plan card.
 *
 * show: true only when:
 *   - clientContext is non-null with upfront_billing_enabled = true
 *   - the plan has upfront_billing_eligible = true
 *   - a positive integer month count is resolvable
 *
 * Month count resolution: clientContext.upfront_months takes precedence;
 * falls back to input.upfront_months (carrier_plan_configs) when the client
 * record carries no override. Price comes from the effective monthly rate.
 */
export function determineUpfrontBilling(
  input: UpfrontBillingInput,
  clientContext: ClientData | null = null,
): UpfrontBillingResult {
  const { upfront_billing_eligible, retail_price, discounted_price } = input;

  if (
    clientContext === null ||
    !clientContext.upfront_billing_enabled ||
    !upfront_billing_eligible
  ) {
    return { show: false, months: null, totalDue: null };
  }

  const months = clientContext.upfront_months ?? input.upfront_months ?? null;
  if (months === null || !Number.isInteger(months) || months <= 0) {
    return { show: false, months: null, totalDue: null };
  }

  const effectivePrice = discounted_price ?? retail_price;
  return {
    show: true,
    months,
    totalDue: effectivePrice * months,
  };
}

/**
 * Entry point for plan surfaces that know the billing interval.
 *
 * Annual billing never qualifies for upfront billing; this wrapper enforces
 * that constraint so callers carry no inline interval logic.
 */
export function defineUpfrontBilling(
  input: UpfrontBillingInput,
  clientContext: ClientData | null,
  billingInterval: 'monthly' | 'annual',
): UpfrontBillingResult {
  if (billingInterval === 'annual') {
    return { show: false, months: null, totalDue: null };
  }
  return determineUpfrontBilling(input, clientContext);
}
